import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { GetSystemPlatformDetailsResponse } from "../../../../common/api/orders-api/airline/GetSystemPlatformDetailsResponse";
import { useAdminAirlineApi } from "../../../airline/common/api/useAdminAirlineApi";
import {
    mapToSystemPlatformDetailsItem,
    SystemPlatformDetailsItem,
    SystemPlatformDetailsEncodeConfigurationItem,
    SystemPlatformDetailsSpecificationItem,
    SystemPlatformDetailsDeliveryInfoItem
} from "../SystemPlatformDetailsItem";
import { SystemPlatformDetailsContext } from "./SystemPlatformDetailsContext";

export const SystemPlatformDetailsProvider: React.FC = ({ children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [systemPlatformDetailsItem, setSystemPlatformDetailsItem] = useState<SystemPlatformDetailsItem>({
        system: {
            id: "",
            name: ""
        },
        integrator: {
            id: "",
            name: ""
        },
        airline: {
            id: "",
            name: ""
        },
        specifications: [],
        encodeConfiguration: {
            muxSpecification: undefined,
            subtitlesTargetFileName: undefined,
            audioProxyType: undefined,
            muxSubtitleType: undefined,
            copySubtitleType: undefined,
            closedCaptionCode: "",
            subtitlesCaptionCode: "",
            languageSpecification: undefined
        },
        deliveryInfo: {
            deliveryEndpoint: undefined,
            distributionList: undefined
        },
        buzzIds: [],
        shreqIds: []
    });

    const { getSystemPlatformDetails } = useAdminAirlineApi();
    const { airlineId } = useParams<{ airlineId: string }>();
    const { systemId } = useParams<{ systemId: string }>();

    useEffect(() => {
        setIsLoading(true);

        getSystemPlatformDetails({ airlineId, systemId })
            .then((response: GetSystemPlatformDetailsResponse) => {
                const airline = {
                    id: airlineId,
                    name: response.airlineName
                };
                const detailsItem = mapToSystemPlatformDetailsItem(response.systemPlatform, airline);
                setSystemPlatformDetailsItem(detailsItem);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const updateSpecification = (updatedSpecification: SystemPlatformDetailsSpecificationItem) => {
        setSystemPlatformDetailsItem((prevState) => {
            return {
                ...prevState,
                specifications: prevState.specifications.map((specification: SystemPlatformDetailsSpecificationItem) =>
                    specification.encodeSpecificationContentType === updatedSpecification.encodeSpecificationContentType
                        ? updatedSpecification
                        : specification
                )
            };
        });
    };

    const updateEncodeConfiguration = (encodeConfiguration: SystemPlatformDetailsEncodeConfigurationItem) => {
        setSystemPlatformDetailsItem((prevState) => ({ ...prevState, encodeConfiguration }));
    };

    const updateDeliveryInfo = (deliveryInfo: SystemPlatformDetailsDeliveryInfoItem) => {
        setSystemPlatformDetailsItem((prevState) => ({ ...prevState, deliveryInfo }));
    };

    const updateBuzzIds = (buzzIds: string[]) => {
        setSystemPlatformDetailsItem((prevState) => ({ ...prevState, buzzIds }));
    };

    const updateShreqIds = (shreqIds: string[]) => {
        setSystemPlatformDetailsItem((prevState) => ({ ...prevState, shreqIds }));
    };

    const resetSpecifications = () => {
        setSystemPlatformDetailsItem((prevState) => {
            return {
                ...prevState,
                specifications: prevState.specifications.map((spec) => ({
                    encodeSpecificationContentType: spec.encodeSpecificationContentType,
                    defaultEncodeSpecificationId: "",
                    defaultEncodeSpecificationName: "",
                    encodeSpecifications: []
                }))
            };
        });
    };

    return (
        <SystemPlatformDetailsContext.Provider
            value={{
                systemPlatformDetailsItem,
                isLoading,
                updateSpecification,
                updateEncodeConfiguration,
                resetSpecifications,
                updateDeliveryInfo,
                updateBuzzIds,
                updateShreqIds
            }}
        >
            {children}
        </SystemPlatformDetailsContext.Provider>
    );
};
